import React, { useState, useEffect, Fragment } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import { CheckCircle2, PlayCircle, Sparkles } from "lucide-react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";
import { toast } from "react-toastify";
import swal from "sweetalert";

// ── Property Helpers ──
const getProductKey = (item) => item?.qbId || item?.ListID || item?.raw?.ListID || item?._id;

const getProductName = (item) =>
  item?.name ||
  item?.Name ||
  item?.fullyQualifiedName ||
  item?.FullName ||
  item?.raw?.Name ||
  item?.raw?.FullName ||
  "Item";

const getProductDesc = (item) =>
  item?.description ??
  item?.Description ??
  item?.raw?.SalesOrPurchase?.Desc ??
  item?.raw?.ItemDesc ??
  "";

const getProductRate = (item) => {
  if (item?.unitPrice != null) return Number(item.unitPrice);
  if (item?.SalesPrice != null) return Number(item.SalesPrice);
  if (item?.raw?.SalesOrPurchase?.Price != null) return Number(item.raw.SalesOrPurchase.Price);
  return 0;
};

const getCustomerKey = (customer) =>
  customer?.qbId || customer?.ListID || customer?.raw?.ListID || customer?._id;

const getCustomerName = (customer) => {
  if (!customer) return "—";
  const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(" ");
  return (
    customer.fullyQualifiedName ||
    customer.FullName ||
    customer.raw?.FullName ||
    customer.name ||
    customer.Name ||
    customer.raw?.Name ||
    fullName ||
    customer.company ||
    customer.raw?.CompanyName ||
    "Unnamed Customer"
  );
};

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val ?? 0);

export default function QBDInvoiceAdd() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.user);
  const companyId = user?.id || user?._id;

  // If navigated from "Convert to Invoice", pre-fill from estimate data
  const estimateData = location.state?.estimate || null;

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [refNumberError, setRefNumberError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const buildLinesFromEstimate = (est) => {
    if (!est) return [{ itemListId: "", description: "", quantity: 1, rate: 0 }];
    const rawLines = est.lines || est.Line || [];
    if (rawLines.length === 0) return [{ itemListId: "", description: "", quantity: 1, rate: 0 }];
    return rawLines.map((l) => ({
      itemListId: l.itemListId || l.ItemListID || l.ItemRef?.ListID || "",
      description: l.description || l.Description || "",
      quantity: Number(l.quantity || l.Quantity || 1),
      rate: Number(l.rate || l.Rate || 0),
    }));
  };

  const [formData, setFormData] = useState({
    customerListId: estimateData?.customerListId || estimateData?.CustomerListID || estimateData?.CustomerRef?.ListID || "",
    linkedEstimateTxnId: estimateData?.txnId || estimateData?.TxnID || "",
    txnDate: new Date().toISOString().split("T")[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    refNumber: estimateData?.refNumber || estimateData?.RefNumber || "",
    lines: buildLinesFromEstimate(estimateData),
  });

  const [errors, setErrors] = useState({});

  const fetchInitialData = async () => {
    loader(true);
    try {
      const [custRes, prodRes] = await Promise.all([
        ApiClient.get(`qb-desktop/list/customers?companyId=${companyId}`),
        ApiClient.get(`qb-desktop/list/items?companyId=${companyId}`),
      ]);
      if (custRes?.success) setCustomers(custRes.data || []);
      if (prodRes?.success) setProducts(prodRes.data || []);
    } catch (err) {
      console.error(err);
    }
    loader(false);
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const subtotal = formData.lines.reduce(
    (acc, l) => acc + Number(l.quantity || 0) * Number(l.rate || 0),
    0
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "refNumber") {
      if (value.length > 11) {
        setRefNumberError("Ref Number cannot exceed 11 characters");
        return;
      } else {
        setRefNumberError("");
      }
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.lines];
    if (field === "itemListId") {
      const product = products.find((p) => getProductKey(p) === value);
      newLines[index].itemListId = value;
      if (product) {
        newLines[index].description = getProductDesc(product);
        newLines[index].rate = getProductRate(product);
      }
    } else {
      newLines[index][field] = value;
    }
    setFormData((prev) => ({ ...prev, lines: newLines }));
  };

  const addLine = () => {
    setFormData((prev) => ({
      ...prev,
      lines: [...prev.lines, { itemListId: "", description: "", quantity: 1, rate: 0 }],
    }));
  };

  const removeLine = (index) => {
    const newLines = formData.lines.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, lines: newLines }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.customerListId) errs.customerListId = "Please select a customer";
    if (!formData.txnDate) errs.txnDate = "Invoice date is required";
    if (!formData.dueDate) errs.dueDate = "Due date is required";
    if (formData.refNumber && formData.refNumber.length > 11)
      errs.refNumber = "Ref Number cannot exceed 11 characters";
    if (formData.lines.length === 0) errs.lines = "At least one line item is required";
    formData.lines.forEach((l, i) => {
      if (!l.itemListId) errs[`line_${i}_item`] = `Select a product for line ${i + 1}`;
      if (!l.quantity || Number(l.quantity) <= 0) errs[`line_${i}_qty`] = `Qty must be > 0 for line ${i + 1}`;
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      swal({ icon: "error", title: "Validation Error", text: "Please fix the errors before submitting." });
      return;
    }

    setSubmitting(true);
    loader(true);
    try {
      const payload = {
        companyId: companyId,
        customerListId: formData.customerListId,
        ...(formData.linkedEstimateTxnId ? { linkedEstimateTxnId: formData.linkedEstimateTxnId } : {}),
        txnDate: formData.txnDate,
        dueDate: formData.dueDate,
        ...(formData.refNumber ? { refNumber: formData.refNumber } : {}),
        lines: formData.lines.map((l) => ({
          itemListId: l.itemListId,
          description: l.description,
          quantity: Number(l.quantity),
          rate: Number(l.rate),
        })),
      };

      const res = await ApiClient.post("qb-desktop/create/invoice", payload);
      if (res?.success) {
        setShowSuccessModal(true);
      } else {
        toast.error(res?.message || "Failed to create invoice");
      }
    } catch (err) {
      toast.error("An error occurred while creating the invoice");
    }
    loader(false);
    setSubmitting(false);
  };

  const handleModalClose = () => {
    setShowSuccessModal(false);
    navigate("/qb-desktop/invoices");
  };

  return (
    <Layout>
      <div className="max-xl:container xl:max-w-[1280px] mx-auto py-5">
        <div className="bg-white shadow-lg rounded-2xl border border-gray-100 px-5 sm:px-8 py-6">
          {/* Header */}
          <div className="flex gap-y-2 gap-x-3 flex-wrap justify-between items-center mb-8 border-b pb-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#115B9D]">
                {estimateData ? "Convert Estimate to Invoice" : "Create Invoice"}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">QuickBooks Desktop</p>
              {estimateData && formData.linkedEstimateTxnId && (
                <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-xl">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                  <span className="text-xs font-medium text-amber-700">
                    Linked to Estimate: {formData.linkedEstimateTxnId}
                  </span>
                </div>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500 font-medium">Subtotal</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(subtotal)}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Customer, Dates, Ref */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {/* Customer */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500 uppercase">
                  Customer <span className="text-red-500">*</span>
                </label>
                <select
                  name="customerListId"
                  value={formData.customerListId}
                  onChange={handleInputChange}
                  className={`h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all bg-white ${errors.customerListId ? "border-red-400" : "border-gray-300"}`}
                >
                  <option value="">Select Customer</option>
                  {customers.map((c, cIdx) => {
                    const cKey = getCustomerKey(c) || cIdx;
                    const cVal = getCustomerKey(c);
                    const cName = getCustomerName(c);
                    return (
                      <option key={cKey} value={cVal}>
                        {cName}
                      </option>
                    );
                  })}
                </select>
                {errors.customerListId && <p className="text-xs text-red-500">{errors.customerListId}</p>}
              </div>

              {/* Invoice Date */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500 uppercase">
                  Invoice Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="txnDate"
                  value={formData.txnDate}
                  onChange={handleInputChange}
                  className={`h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all ${errors.txnDate ? "border-red-400" : "border-gray-300"}`}
                />
                {errors.txnDate && <p className="text-xs text-red-500">{errors.txnDate}</p>}
              </div>

              {/* Due Date */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500 uppercase">
                  Due Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleInputChange}
                  className={`h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all ${errors.dueDate ? "border-red-400" : "border-gray-300"}`}
                />
                {errors.dueDate && <p className="text-xs text-red-500">{errors.dueDate}</p>}
              </div>

              {/* Ref Number */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500 uppercase">
                  Ref Number
                  <span className="ml-1 text-[10px] text-gray-400 normal-case">(max 11 chars)</span>
                </label>
                <input
                  type="text"
                  name="refNumber"
                  value={formData.refNumber}
                  onChange={handleInputChange}
                  placeholder="e.g. 17-1784553"
                  maxLength={11}
                  className={`h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all ${refNumberError || errors.refNumber ? "border-red-400" : "border-gray-300"}`}
                />
                <div className="flex justify-between">
                  {(refNumberError || errors.refNumber) && (
                    <p className="text-xs text-red-500">{refNumberError || errors.refNumber}</p>
                  )}
                  <p className="text-[10px] text-gray-400 ml-auto">{formData.refNumber.length}/11</p>
                </div>
              </div>

              {/* Linked Estimate TxnId */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500 uppercase">Linked Estimate TxnID</label>
                <input
                  type="text"
                  name="linkedEstimateTxnId"
                  value={formData.linkedEstimateTxnId}
                  onChange={handleInputChange}
                  placeholder="e.g. 17-1784553206"
                  className="h-10 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
                />
                <span className="text-[10px] text-gray-400">Optional – links invoice to an existing estimate</span>
              </div>
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800">Line Items</h2>
                <button
                  type="button"
                  onClick={addLine}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#115B9D]/10 text-[#115B9D] text-sm font-medium hover:bg-[#115B9D]/20 transition-all"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14" /><path d="M12 5v14" />
                  </svg>
                  Add Line
                </button>
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded-2xl">
                <table className="w-full text-sm min-w-[700px]">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600 w-8">#</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Product/Service</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Description</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600 w-24">Qty</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600 w-28">Rate</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600 w-28">Amount</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-600 w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {formData.lines.map((line, index) => {
                      const amount = Number(line.quantity || 0) * Number(line.rate || 0);
                      return (
                        <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-4 py-3 text-gray-500 text-xs">{index + 1}</td>
                          <td className="px-4 py-3">
                            <select
                              value={line.itemListId}
                              onChange={(e) => handleLineChange(index, "itemListId", e.target.value)}
                              className={`h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all bg-white min-w-[150px] ${errors[`line_${index}_item`] ? "border-red-400" : "border-gray-300"}`}
                            >
                              <option value="">Select Product</option>
                              {products.map((p, pIdx) => {
                                const pKey = getProductKey(p) || pIdx;
                                const pVal = getProductKey(p);
                                const pName = getProductName(p);
                                return (
                                  <option key={pKey} value={pVal}>
                                    {pName}
                                  </option>
                                );
                              })}
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={line.description}
                              onChange={(e) => handleLineChange(index, "description", e.target.value)}
                              className="h-10 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all min-w-[140px]"
                              placeholder="Description"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              min="1"
                              value={line.quantity}
                              onChange={(e) => handleLineChange(index, "quantity", e.target.value)}
                              className={`h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all text-right min-w-[65px] ${errors[`line_${index}_qty`] ? "border-red-400" : "border-gray-300"}`}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={line.rate}
                              onChange={(e) => handleLineChange(index, "rate", e.target.value)}
                              className="h-10 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all text-right min-w-[90px]"
                            />
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-800 whitespace-nowrap">
                            {formatCurrency(amount)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {formData.lines.length > 1 && (
                              <button type="button" onClick={() => removeLine(index)} className="text-red-400 hover:text-red-600 transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                </svg>
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="flex justify-end mt-5">
                <div className="bg-gray-50 rounded-2xl p-5 w-full max-w-xs space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Subtotal</span>
                    <span className="text-sm font-semibold text-gray-800">{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="flex justify-between items-center border-t pt-3">
                    <span className="text-base font-semibold text-gray-900">Total</span>
                    <span className="text-base font-bold text-[#115B9D]">{formatCurrency(subtotal)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#115B9D] hover:opacity-80 text-white text-sm font-medium transition-all disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin size-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Creating...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
                    </svg>
                    {estimateData ? "Convert to Invoice" : "Create Invoice"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Created Success & Web Connector Guidance Modal */}
      <Transition appear show={showSuccessModal} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            /* Closed only when clicking Okay Got It */
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-xs" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6 border border-gray-200 text-left">
                  {/* Modal Header */}
                  <div className="flex items-start justify-between mb-4 pb-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-xl bg-[#F0FEF4] border border-[#0a7822a6]/20 text-[#008236] flex items-center justify-center shrink-0">
                        <CheckCircle2 className="size-5" />
                      </div>
                      <div>
                        <Dialog.Title className="text-base font-semibold text-gray-900">
                          Invoice Created Successfully!
                        </Dialog.Title>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Queued for QuickBooks Desktop Web Connector
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Modal Body */}
                  <div className="space-y-4">
                    <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
                      <p className="text-xs font-semibold text-[#115B9D] uppercase tracking-wide mb-3 flex items-center gap-1.5">
                        <PlayCircle className="size-4" /> Sync Instructions
                      </p>

                      <div className="space-y-2.5">
                        <div className="flex items-start gap-2.5">
                          <span className="size-5 rounded-md bg-[#115B9D] text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                            1
                          </span>
                          <p className="text-xs text-gray-700 leading-5">
                            If <strong>Auto-Run</strong> is not enabled in Web Connector, open <strong>QuickBooks Web Connector</strong> on your PC.
                          </p>
                        </div>

                        <div className="flex items-start gap-2.5">
                          <span className="size-5 rounded-md bg-[#115B9D] text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                            2
                          </span>
                          <p className="text-xs text-gray-700 leading-5">
                            Check the box <strong className="text-[#115B9D]">[ ✓ ]</strong> next to your application name and click <strong className="text-[#008236]">"Update Selected"</strong> to manually sync this invoice.
                          </p>
                        </div>

                        <div className="flex items-start gap-2.5">
                          <span className="size-5 rounded-md bg-[#115B9D] text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                            3
                          </span>
                          <p className="text-xs text-gray-700 leading-5">
                            Once synced, the invoice status and details will be updated in your desktop invoice listing.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-3 rounded-xl bg-amber-50/80 border border-amber-200/80 flex items-start gap-2.5 text-xs text-amber-900 leading-5">
                      <Sparkles className="size-4 text-amber-600 mt-0.5 shrink-0" />
                      <span>
                        <strong>Pro Tip:</strong> Enable the <strong>Auto-Run</strong> checkbox in Web Connector to automatically sync new invoices every 15 minutes.
                      </span>
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="mt-5 pt-3 border-t border-gray-100 flex justify-end">
                    <button
                      type="button"
                      onClick={handleModalClose}
                      className="inline-flex items-center justify-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-xs font-medium px-5 py-2.5 rounded-xl transition-all"
                    >
                      Okay, Got it!
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Layout>
  );
}

